import type { MetaFunction } from '@remix-run/node'
import { Link } from '@remix-run/react'
import { ArrowRight } from 'lucide-react'
import { buttonVariants } from '#app/components/ui/button'
import { cn } from '#app/utils/misc'
import { siteConfig } from '#app/utils/constants/brand'
import { ROUTE_PATH as LOGIN_PATH } from '#app/routes/auth+/login'

export const ROUTE_PATH = '/guides/webhook-to-send-email' as const

const TITLE = 'Webhook to Send Email: Complete Guide (Both Directions) | InboxBridge'
const DESCRIPTION =
  'A complete tutorial on sending email when a webhook fires — Node.js/Express with Resend, idempotency keys, and retries — plus the reverse: receiving inbound email as a JSON webhook.'
const URL = `${siteConfig.siteUrl}/guides/webhook-to-send-email`

// Pinned manually so dateModified only changes when the content actually does.
// Bump dateModified when you make substantive edits to the guide body.
const DATE_PUBLISHED = '2026-07-12'
const DATE_MODIFIED = '2026-07-12'

const ARTICLE_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'TechArticle',
  headline:
    'Webhook to Send Email: How to Trigger Emails from Webhooks (and the Reverse)',
  description: DESCRIPTION,
  image: siteConfig.siteImageUrl,
  url: URL,
  datePublished: DATE_PUBLISHED,
  dateModified: DATE_MODIFIED,
  author: {
    '@type': 'Organization',
    name: siteConfig.siteTitle,
    url: siteConfig.siteUrl,
  },
  publisher: {
    '@type': 'Organization',
    name: siteConfig.siteTitle,
    url: siteConfig.siteUrl,
    logo: { '@type': 'ImageObject', url: siteConfig.siteImageUrl },
  },
  mainEntityOfPage: { '@type': 'WebPage', '@id': URL },
}

const BREADCRUMB_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: siteConfig.siteUrl },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Webhook to send email: how to trigger emails from webhooks (and the reverse)',
      item: URL,
    },
  ],
}

const FAQS = [
  {
    question: 'Can a webhook send an email?',
    answer:
      'Not by itself — a webhook is just an HTTP POST to a URL. To send an email from a webhook, you point the webhook at an endpoint you control (an Express route, a serverless function, or a no-code tool like Zapier), and that endpoint calls an email API such as Resend, SendGrid, Postmark, or Amazon SES. The webhook is the trigger; the email API does the sending.',
  },
  {
    question: 'How do I get an email into a webhook?',
    answer:
      'Use an inbound email parsing service. InboxBridge gives you a unique email address; any message sent to it is parsed into structured JSON (sender, subject, text and HTML bodies, attachments) and POSTed to your webhook URL, with signed requests and automatic retries. You never touch MX records, SMTP, or MIME parsing.',
  },
  {
    question: 'Do I need my own mail server?',
    answer:
      'No, for either direction. For sending, transactional email APIs handle SMTP, deliverability, SPF/DKIM, and bounce processing for you. For receiving, an email-to-webhook service like InboxBridge runs the mail infrastructure and hands your application clean JSON over HTTP. Running your own mail server is almost never worth the operational cost.',
  },
]

const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
}

export const meta: MetaFunction = () => {
  return [
    { title: TITLE },
    { name: 'description', content: DESCRIPTION },
    { tagName: 'link', rel: 'canonical', href: URL },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: URL },
    { property: 'og:title', content: TITLE },
    { property: 'og:description', content: DESCRIPTION },
    { property: 'og:image', content: siteConfig.siteImageUrl },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: TITLE },
    { name: 'twitter:description', content: DESCRIPTION },
    { 'script:ld+json': ARTICLE_JSON_LD },
    { 'script:ld+json': BREADCRUMB_JSON_LD },
    { 'script:ld+json': FAQ_JSON_LD },
  ]
}

const codeBlockClass =
  'overflow-x-auto rounded-lg border border-border bg-secondary/40 p-4 text-sm text-muted-foreground'

export default function WebhookToSendEmailGuide() {
  return (
    <main className="mx-auto flex w-full max-w-screen-md flex-col gap-8 px-6 py-20">
      <header className="flex flex-col gap-4">
        <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Guide
        </p>
        <h1 className="text-4xl font-bold tracking-tight text-primary md:text-5xl">
          Webhook to Send Email: How to Trigger Emails from Webhooks (and the Reverse)
        </h1>
        <p className="text-lg leading-relaxed text-muted-foreground">
          &ldquo;Webhook to send email&rdquo; is one of those searches that means two
          opposite things depending on who is typing it:
        </p>
        <ol className="ml-5 flex list-decimal flex-col gap-1 leading-relaxed text-muted-foreground">
          <li>
            <a
              href="#send-email-from-webhook"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Triggering an outgoing email when a webhook fires
            </a>{' '}
            — a payment succeeds, a form is submitted, a deploy finishes, and you want an
            email to go out.
          </li>
          <li>
            <a
              href="#receive-email-as-webhook"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Receiving an incoming email as a webhook
            </a>{' '}
            — someone sends an email to an address, and you want it delivered to your API
            as a JSON POST.
          </li>
        </ol>
        <p className="leading-relaxed text-muted-foreground">
          This guide covers both directions properly, with working code. If you are here
          for #2 — turning inbound email into webhooks — that is exactly what{' '}
          <Link
            to="/email-to-webhook"
            prefetch="intent"
            className="font-medium text-primary underline underline-offset-4 hover:no-underline">
            InboxBridge
          </Link>{' '}
          does, and you can jump straight to that section.
        </p>
      </header>

      <section id="send-email-from-webhook" className="flex scroll-mt-24 flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Part 1: Send an email when a webhook fires
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          A webhook on its own cannot send anything — it is just an HTTP POST from some
          system (Stripe, GitHub, Shopify, your form provider) to a URL you give it. To
          turn that POST into an email, you need a small piece of glue in the middle. The
          architecture is always the same three boxes:
        </p>
        <pre className={codeBlockClass}>
          {`┌─────────────────┐      ┌──────────────────┐      ┌─────────────────┐
│   Event source   │ POST │   Your endpoint   │ API  │    Email API     │
│ (Stripe, GitHub, ├─────►│ (Express route or ├─────►│ (Resend, SendGrid│
│  form, CI, ...)  │      │  serverless fn)   │      │  Postmark, SES)  │
└─────────────────┘      └──────────────────┘      └─────────────────┘`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          The event source fires the webhook. Your endpoint receives it, decides what the
          email should say, and calls a transactional email API to actually send it. You
          do not run a mail server at any point — deliverability, SPF/DKIM, bounce
          handling, and the SMTP conversation are all the email API&apos;s problem.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          We will build the endpoint in Node.js with Express and send through{' '}
          <a
            href="https://resend.com"
            rel="noopener noreferrer"
            className="font-medium text-primary underline underline-offset-4 hover:no-underline">
            Resend
          </a>
          , because its API is the smallest to demonstrate. Everything here translates
          directly to SendGrid, Postmark, or Amazon SES — only the client library and the
          shape of the send call change.
        </p>

        <h3 className="text-xl font-semibold text-primary">
          Step 1: A minimal receiving endpoint
        </h3>
        <p className="leading-relaxed text-muted-foreground">
          Start with an Express app that accepts a webhook POST and sends one email. This
          example imagines an &ldquo;order created&rdquo; event, but the pattern is
          identical for any source:
        </p>
        <pre className={codeBlockClass}>
          {`// server.js
import express from "express";
import { Resend } from "resend";

const app = express();
const resend = new Resend(process.env.RESEND_API_KEY);

app.post("/webhooks/order-created", express.json(), async (req, res) => {
  const event = req.body;

  await resend.emails.send({
    from: "Orders <orders@yourdomain.com>",
    to: event.customerEmail,
    subject: \`Order \${event.orderId} confirmed\`,
    html: \`<p>Thanks! Your order <strong>\${event.orderId}</strong> is confirmed.</p>\`,
  });

  res.status(200).json({ ok: true });
});

app.listen(3000);`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Point your event source at{' '}
          <code>https://your-server.com/webhooks/order-created</code>, and every event
          produces an email. That is the whole idea — but this version has two real bugs
          waiting to happen, and fixing them is what separates a demo from something you
          can run in production.
        </p>

        <h3 className="text-xl font-semibold text-primary">
          Step 2: Idempotency — the duplicate-email bug
        </h3>
        <p className="leading-relaxed text-muted-foreground">
          Webhook providers deliver <em>at least once</em>, not exactly once. If your
          endpoint is slow to respond, times out, or returns a non-2xx during a blip, the
          provider retries — and the naive handler above will happily email your customer
          twice. Stripe, GitHub, and Shopify all document this behavior explicitly; it is
          not an edge case, it is the contract.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          The fix has two layers. First, dedupe on the event&apos;s stable ID (every
          serious webhook provider includes one — <code>event.id</code> in Stripe,{' '}
          <code>X-GitHub-Delivery</code> in GitHub) so a retried delivery is recognized
          and skipped. Second, pass an <strong>idempotency key</strong> to the email API
          itself, so that even if your dedupe store misses (a race between two concurrent
          retries, a restart that wiped an in-memory set), the email provider refuses to
          send the same message twice. Resend accepts an idempotency key directly on the
          send call:
        </p>
        <pre className={codeBlockClass}>
          {`// A tiny dedupe store. In production use Redis with a TTL,
// or a database table with a unique constraint on eventId.
const processedEvents = new Set();

app.post("/webhooks/order-created", express.json(), async (req, res) => {
  const event = req.body;
  const eventId = event.id; // the provider's stable event ID

  // Layer 1: skip deliveries we've already handled.
  if (processedEvents.has(eventId)) {
    return res.status(200).json({ ok: true, duplicate: true });
  }

  const { error } = await resend.emails.send(
    {
      from: "Orders <orders@yourdomain.com>",
      to: event.customerEmail,
      subject: \`Order \${event.orderId} confirmed\`,
      html: \`<p>Thanks! Your order <strong>\${event.orderId}</strong> is confirmed.</p>\`,
    },
    // Layer 2: the email API dedupes on this key for ~24h.
    { idempotencyKey: \`order-confirmed/\${eventId}\` }
  );

  // Resend's SDK doesn't throw on failure — it returns { data, error }.
  // A failed send must not mark the event processed; return a 500 so the
  // webhook provider redelivers (more on retries in the next section).
  if (error) {
    return res.status(500).json({ error: "email send failed" });
  }

  processedEvents.add(eventId);
  res.status(200).json({ ok: true });
});`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Two details matter here. Mark the event as processed <em>after</em> the send
          succeeds, not before — otherwise a crash mid-send leaves the event marked done
          with no email sent. And derive the idempotency key from the event ID plus the
          purpose (<code>order-confirmed/evt_123</code>), so the same event can still
          legitimately trigger two <em>different</em> emails (say, a customer receipt and
          an internal notification) without one blocking the other.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          If you use SendGrid or SES, which do not expose idempotency keys on the send
          call, the database layer does all the work: insert the event ID into a table
          with a unique constraint before sending, and let the constraint violation tell
          you it is a duplicate.
        </p>

        <h3 className="text-xl font-semibold text-primary">
          Step 3: Retry handling — in both directions
        </h3>
        <p className="leading-relaxed text-muted-foreground">
          There are two retry loops to think about, and they point in opposite directions.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          <strong>Retries from the event source to you.</strong> Lean on these — they are
          free reliability. If the email API is down or returns a 5xx, respond to the
          webhook with a <code>500</code> and let the provider redeliver later. Because
          you have idempotency in place, the retry is safe. The one rule: respond quickly.
          If building the email involves slow work (fetching order details, rendering a
          template), acknowledge the webhook first and do the work in a queue or
          background job so you never trip the provider&apos;s timeout.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          <strong>Retries from you to the email API.</strong> Transient failures — a
          network hiccup, a <code>429</code> rate limit — deserve a couple of immediate
          retries with backoff before you give up and lean on the webhook provider&apos;s
          slower retry schedule. One SDK-specific trap: Resend never throws on failure.
          Every call resolves to <code>{'{ data, error }'}</code> — even network errors
          come back in <code>error</code> — so a <code>try/catch</code> around the send
          will never fire. Check <code>error</code> explicitly:
        </p>
        <pre className={codeBlockClass}>
          {`async function sendWithRetry(payload, options, attempts = 3) {
  for (let i = 0; i < attempts; i++) {
    const { data, error } = await resend.emails.send(payload, options);
    if (!error) return data;

    // statusCode is null on network-level failures — retry those too.
    const retryable =
      error.statusCode === null ||
      error.statusCode === 429 ||
      error.statusCode >= 500;
    if (!retryable || i === attempts - 1) {
      throw new Error(\`Email send failed (\${error.name}): \${error.message}\`);
    }
    // 500ms, 1s, 2s — with the idempotency key, retries can't double-send.
    await new Promise((r) => setTimeout(r, 500 * 2 ** i));
  }
}`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Do not retry on 4xx errors other than 429 — a rejected recipient address or a
          malformed payload will fail identically every time, and retrying just burns
          quota.
        </p>

        <h3 className="text-xl font-semibold text-primary">
          Step 4: Verify the webhook is genuine
        </h3>
        <p className="leading-relaxed text-muted-foreground">
          Your endpoint is a public URL that sends email on demand — exactly the kind of
          thing you do not want strangers to be able to trigger. Every reputable webhook
          provider signs its deliveries: Stripe with <code>Stripe-Signature</code>, GitHub
          with <code>X-Hub-Signature-256</code>, most others with some HMAC over the raw
          request body. Verify the signature before doing anything else, and read the body
          as raw bytes first, because the HMAC is computed over the exact bytes sent:
        </p>
        <pre className={codeBlockClass}>
          {`import crypto from "crypto";

// Generic HMAC-SHA256 verification; check your provider's header name
// and format. For Stripe specifically, use stripe.webhooks.constructEvent.
app.post(
  "/webhooks/order-created",
  express.raw({ type: "application/json" }),
  async (req, res) => {
    const signature = req.headers["x-webhook-signature"] ?? "";
    const expected = crypto
      .createHmac("sha256", process.env.WEBHOOK_SECRET)
      .update(req.body) // raw Buffer, not parsed JSON
      .digest("hex");

    const a = Buffer.from(String(signature));
    const b = Buffer.from(expected);
    if (a.length !== b.length || !crypto.timingSafeEqual(a, b)) {
      return res.status(401).json({ error: "Invalid signature" });
    }

    const event = JSON.parse(req.body.toString("utf8"));
    // ... dedupe, send, respond as above
  }
);`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Use a timing-safe comparison, never <code>===</code>, and store the secret in an
          environment variable. If a provider offers an official verification helper
          (Stripe and GitHub both do), prefer it over hand-rolling.
        </p>

        <h3 className="text-xl font-semibold text-primary">
          The alternatives: SendGrid, Postmark, SES
        </h3>
        <p className="leading-relaxed text-muted-foreground">
          Nothing above is Resend-specific. With <strong>SendGrid</strong> you call{' '}
          <code>sgMail.send()</code> with a nearly identical payload; it is the incumbent,
          with a large free tier and heavier dashboard. <strong>Postmark</strong> is the
          deliverability-obsessed option — strictly transactional, excellent bounce
          visibility, and the same provider InboxBridge uses for inbound mail.{' '}
          <strong>Amazon SES</strong> is the cheapest at volume by a wide margin, at the
          cost of more setup (IAM, sandbox exit, reputation management is on you). If you
          are sending a handful of notification emails from webhooks, pick whichever you
          already have credentials for; the architecture does not change.
        </p>

        <h3 className="text-xl font-semibold text-primary">
          The no-code alternative: Zapier or Make
        </h3>
        <p className="leading-relaxed text-muted-foreground">
          If this email is an internal notification and nobody on the team wants to
          maintain an endpoint, Zapier&apos;s &ldquo;Webhooks by Zapier&rdquo; trigger (or
          Make&apos;s custom webhook module) plus an email action does the whole job
          without code: the tool gives you a catch URL, you point the event source at it,
          map fields into an email template, and you are done in ten minutes. The
          tradeoffs are per-task pricing that scales with volume, limited control over
          retries and dedupe, and templates that live in a dashboard instead of version
          control. For a low-volume internal alert, that trade is often fine; for anything
          customer-facing, own the code.
        </p>
      </section>

      <section id="receive-email-as-webhook" className="flex scroll-mt-24 flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          Part 2: Receive an email as a webhook
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          Now flip the arrow. Instead of a webhook triggering an email, an email triggers
          a webhook: someone sends a message to an address, and your API receives it as a
          JSON POST. This is how you build reply-by-email, support inboxes that create
          tickets, document-ingestion addresses, email-driven bots — anything where email
          is the input.
        </p>

        <h3 className="text-xl font-semibold text-primary">
          Why this direction is the hard one
        </h3>
        <p className="leading-relaxed text-muted-foreground">
          Sending email from code is a solved, one-API-call problem. Receiving it is not,
          because to accept email for an address you nominally need to <em>be</em> a mail
          server: publish MX records for a domain, run an SMTP listener that speaks the
          protocol to every mail server on the internet, survive spam and malformed
          traffic, and then parse what arrives. And what arrives is MIME — a decades-old,
          nested, inconsistently-implemented format where the plain-text body, the HTML
          body, inline images, and attachments are tangled together in multipart trees,
          with several competing encodings and header quirks that vary by sending client.
          Getting from &ldquo;raw RFC 5322 message&rdquo; to &ldquo;the sender, the
          subject, the body as a string&rdquo; is genuinely unpleasant work, and running
          the infrastructure behind it is a permanent operational tax.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          An email-to-webhook service exists to delete that entire layer. InboxBridge runs
          the mail infrastructure, does the parsing, and hands your application the one
          thing it wanted all along: a clean JSON object at a URL you choose. Setup is
          about five minutes:
        </p>

        <h3 className="text-xl font-semibold text-primary">Step 1: Create a bridge</h3>
        <p className="leading-relaxed text-muted-foreground">
          A bridge connects an inbound email address to your webhook URL.{' '}
          <Link
            to={LOGIN_PATH}
            className="font-medium text-primary underline underline-offset-4 hover:no-underline">
            Sign up
          </Link>{' '}
          (the free plan covers 1,000 emails a month, no card required), create a bridge,
          and you get a unique address like <code>abc123@webhook.inboxbridge.io</code>.
          Point the bridge at your endpoint — say{' '}
          <code>https://your-app.com/webhooks/inbound-email</code> — and every message
          sent to that address is parsed and POSTed there. Each bridge also gets a signing
          secret, used to sign every delivery.
        </p>

        <h3 className="text-xl font-semibold text-primary">Step 2: Know the payload</h3>
        <p className="leading-relaxed text-muted-foreground">
          When an email arrives, your endpoint receives JSON in this shape — addresses
          already split into <code>email</code> and <code>name</code>, bodies already
          separated into text and HTML, attachments already described:
        </p>
        <pre className={codeBlockClass}>
          {`{
  "id": "9b2c1f7a4e",
  "receivedAt": "2026-07-12T15:04:22.000Z",
  "from": { "email": "jordan@acme.com", "name": "Jordan Lee" },
  "to": [{ "email": "abc123@webhook.inboxbridge.io", "name": "" }],
  "cc": [],
  "bcc": [],
  "replyTo": null,
  "subject": "Question about invoice #1234",
  "date": "2026-07-12T15:04:00.000Z",
  "textBody": "Hi, can someone look into invoice #1234?",
  "htmlBody": "<p>Hi, can someone look into invoice #1234?</p>",
  "attachments": []
}`}
        </pre>

        <h3 className="text-xl font-semibold text-primary">Step 3: Handle it</h3>
        <p className="leading-relaxed text-muted-foreground">
          InboxBridge signs each delivery with an HMAC-SHA256 of the raw body, sent in the{' '}
          <code>X-InboxBridge-Signature</code> header as <code>sha256=…</code>. The quickest way
          to verify and parse it is our open-source package,{' '}
          <a
            href="https://www.npmjs.com/package/inbound-email-webhook"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-primary underline underline-offset-4 hover:no-underline">
            inbound-email-webhook
          </a>
          . Its Express middleware checks the signature and attaches a typed email to{' '}
          <code>req.inboundEmail</code>:
        </p>
        <pre className={codeBlockClass}>{`npm install inbound-email-webhook`}</pre>
        <pre className={codeBlockClass}>
          {`import express from "express";
import {
  inboundEmailWebhook,
  type InboundEmailRequest,
} from "inbound-email-webhook/express";

app.post(
  "/webhooks/inbound-email",
  express.raw({ type: "application/json" }),
  inboundEmailWebhook({ secret: process.env.INBOXBRIDGE_SIGNING_SECRET! }),
  (req, res) => {
    const email = (req as InboundEmailRequest).inboundEmail!;

    console.log(\`Email from \${email.from.email}: \${email.subject}\`);
    // Create a ticket, post to Slack, kick off a job — it's just JSON now.

    res.status(200).json({ ok: true });
  }
);`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Prefer not to add a dependency? The manual handler mirrors the outbound one from
          Part 1, with the signature verification you already know how to write:
        </p>
        <pre className={codeBlockClass}>
          {`import crypto from "crypto";

app.post(
  "/webhooks/inbound-email",
  express.raw({ type: "application/json" }),
  async (req, res) => {
    const signature = req.headers["x-inboxbridge-signature"] ?? "";
    const expected =
      "sha256=" +
      crypto
        .createHmac("sha256", process.env.INBOXBRIDGE_SIGNING_SECRET)
        .update(req.body)
        .digest("hex");

    const a = Buffer.from(String(signature));
    const b = Buffer.from(expected);
    if (a.length !== b.length || !crypto.timingSafeEqual(a, b)) {
      return res.status(401).json({ error: "Invalid signature" });
    }

    const email = JSON.parse(req.body.toString("utf8"));

    console.log(\`Email from \${email.from.email}: \${email.subject}\`);
    // Create a ticket, post to Slack, kick off a job — it's just JSON now.

    res.status(200).json({ ok: true });
  }
);`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          Return a 2xx quickly. If your endpoint is down or returns an error, InboxBridge
          retries the delivery with exponential backoff and records every attempt — its
          status code and response time — in a delivery log you can inspect. The same
          idempotency thinking from Part 1 applies in this direction too: every payload
          carries a stable <code>id</code> you can dedupe on if a double-delivery would
          matter.
        </p>
        <p className="leading-relaxed text-muted-foreground">
          That is the whole setup. For the step-by-step version with delivery logs,
          testing, and local-development tips, see the{' '}
          <Link
            to="/guides/inbound-email-webhook"
            prefetch="intent"
            className="font-medium text-primary underline underline-offset-4 hover:no-underline">
            inbound email webhook tutorial
          </Link>
          , or the{' '}
          <Link
            to="/email-to-webhook"
            prefetch="intent"
            className="font-medium text-primary underline underline-offset-4 hover:no-underline">
            email to webhook overview
          </Link>{' '}
          for what the service does end to end.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          The two directions, side by side
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="py-3 pr-4 font-semibold text-primary"></th>
                <th className="py-3 pr-4 font-semibold text-primary">
                  Webhook → Email (outbound)
                </th>
                <th className="py-3 font-semibold text-primary">
                  Email → Webhook (inbound)
                </th>
              </tr>
            </thead>
            <tbody className="text-muted-foreground">
              <tr className="border-b border-border/60">
                <td className="py-3 pr-4 font-medium text-primary">Trigger</td>
                <td className="py-3 pr-4">
                  An event in another system (payment, deploy, form submission) POSTs to
                  your endpoint
                </td>
                <td className="py-3">
                  A person or system sends an email to an address you hand out
                </td>
              </tr>
              <tr className="border-b border-border/60">
                <td className="py-3 pr-4 font-medium text-primary">Tooling</td>
                <td className="py-3 pr-4">
                  An HTTP endpoint plus a transactional email API (Resend, SendGrid,
                  Postmark, SES) — or Zapier/Make for no-code
                </td>
                <td className="py-3">
                  An email-to-webhook service (InboxBridge) plus an HTTP endpoint — or MX
                  records, an SMTP server, and a MIME parser if you insist
                </td>
              </tr>
              <tr className="border-b border-border/60">
                <td className="py-3 pr-4 font-medium text-primary">Hard part</td>
                <td className="py-3 pr-4">
                  Idempotency and retries so duplicate deliveries never double-send
                </td>
                <td className="py-3">
                  Receiving and parsing mail at all — which the service does for you
                </td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-primary">Use cases</td>
                <td className="py-3 pr-4">
                  Order confirmations, payment receipts, deploy/CI alerts, form
                  notifications, digest emails
                </td>
                <td className="py-3">
                  Reply-by-email, support ticket creation, document ingestion,
                  email-driven bots, forwarding mail into Slack or a database
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="leading-relaxed text-muted-foreground">
          Plenty of real systems need both: a support tool receives customer email as a
          webhook, creates a ticket, and then sends the agent&apos;s reply back out
          through an email API. Once both directions are just HTTP, composing them is
          ordinary application code.
        </p>
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-2xl font-semibold text-primary">FAQ</h2>
        {FAQS.map((faq) => (
          <div key={faq.question} className="flex flex-col gap-2">
            <h3 className="text-xl font-semibold text-primary">{faq.question}</h3>
            <p className="leading-relaxed text-muted-foreground">{faq.answer}</p>
          </div>
        ))}
      </section>

      <div className="flex flex-wrap gap-3">
        <Link
          to={LOGIN_PATH}
          className={cn(buttonVariants({ size: 'lg' }), 'gap-2 font-semibold')}>
          Turn email into webhooks
          <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/email-to-webhook"
          className={cn(buttonVariants({ variant: 'outline', size: 'lg' }))}>
          Email to webhook overview
        </Link>
      </div>

      <section className="mt-8 border-t border-border/40 pt-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-primary">
          Related
        </h2>
        <ul className="flex flex-col gap-2 text-base text-muted-foreground">
          <li>
            <Link
              to="/guides/inbound-email-webhook"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Inbound email webhook tutorial
            </Link>
            . The five-minute setup for the inbound direction, framework-agnostic.
          </li>
          <li>
            <Link
              to="/guides/receive-inbound-email-as-json-nextjs"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Receive inbound email as JSON in Next.js
            </Link>
            . The same flow with an App Router route handler.
          </li>
          <li>
            <Link
              to="/guides/forward-email-to-slack-without-zapier"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Forward email to Slack (without Zapier)
            </Link>
            . A complete inbound build with filtering and Block Kit formatting.
          </li>
          <li>
            <Link
              to="/email-to-webhook"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Email to webhook overview
            </Link>
            . What the service does and what payloads look like.
          </li>
        </ul>
      </section>
    </main>
  )
}
